import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import Coupon from '@components/membership/coupon/Coupon'
import Plan from '@components/membership/plan/Plan'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Coupon as CouponModel } from '@models/membership/Coupon'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getCoupons,
  selectCoupons,
  setCouponQuery,
} from '@store/membership/CouponReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { currencyFormat } from '@utils/currencyFormatter'
import { Permission } from '@utils/permissions'
import { Input, Space, TableColumnsType, Tag } from 'antd'
import { useEffect, useRef } from 'react'

const CouponsPage = () => {
  const dispatch = useAppDispatch()
  const { isOpen } = useSBRM()
  const coupons = useAppSelector(selectCoupons())
  const { query, isLoading } = useAppSelector((state) => state.coupon)

  const columns: TableColumnsType<CouponModel> = [
    {
      title: 'Code',
      render: (record: CouponModel) => <Coupon.Avatar model={record} />,
    },
    {
      title: 'Amount',
      render: (record: CouponModel) => (
        <Space wrap>
          {record.fullAmount ? (
            <Tag>100%</Tag>
          ) : (
            <Tag>{currencyFormat(record.fixedAmount, true, true, true)}</Tag>
          )}
          {record.useOnlyOnFirstCycle === true && (
            <Tag>Only on first cycle</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Usage',
      render: (record: CouponModel) => (
        <Tag>
          {record.usage} / {record.maxUsage}
        </Tag>
      ),
    },
    {
      title: 'Plans',
      width: 200,
      render: (record: CouponModel) => (
        <Space wrap>
          {record.plans?.map((id) => (
            <Plan.Tag key={id} id={id} />
          ))}
        </Space>
      ),
    },
    {
      align: 'right',
      render: (record: CouponModel) => (
        <SBRM.Actions
          entity={Entity.coupon}
          entityId={record.id}
          actions={[
            SBRMAction.Update,
            ...(record.usage === 0 ? [SBRMAction.Delete] : []),
          ]}
        />
      ),
    },
  ]

  const fetchCoupons = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
    }

    dispatch(setCouponQuery(newQuery))
    dispatch(getCoupons(newQuery))
  }

  const { handleTableChange, params } = useTableUrlSync<CouponModel>({
    onParamsChange: fetchCoupons,
  })

  // useTableUrlSync already fetches on mount: refetch only when a create,
  // update or delete modal closes, so opening the page sends one request.
  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = isOpen
    if (isOpen || !wasOpen) return

    fetchCoupons(params)
  }, [isOpen])

  return (
    <Page
      permissions={Permission.manageCouponsMembership}
      pageTitle={{
        title: 'Coupon codes',
        level: 4,
      }}
    >
      <TableFilters
        initialValues={{ code: query.search?.code }}
        onValuesChange={(filters, search) => {
          const newQuery: TableParams = {
            ...query,
            pagination: { ...query.pagination, current: 1 },
            columnKey: 'code',
            order: 'ascend',
            filters: filters,
            search: search,
          }

          dispatch(setCouponQuery(newQuery))
          dispatch(getCoupons(newQuery))
        }}
        left={[
          {
            name: 'plans',
            type: 'filter',
            label: 'Plans',
            input: (
              <SBAsyncSelect
                style={{ minWidth: '16ch' }}
                multiple
                treeSelectProps={{
                  maxTagCount: 'responsive',
                }}
                placeholder="Select plans"
                entity={Entity.membershipPlan}
                allowClear
              />
            ),
          },
          {
            name: 'code',
            type: 'search',
            input: <Input placeholder="Search by code" allowClear />,
          },
        ]}
        right={[
          {
            type: 'button',
            input: (
              <SBRM.Actions
                actions={[SBRMAction.Create]}
                entity={Entity.coupon}
              />
            ),
          },
        ]}
      />
      <SBTable
        columns={columns}
        dataSource={coupons}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={query.pagination}
      />
    </Page>
  )
}

export default CouponsPage
